import { IField, IFormDocument } from 'app/services/api5-service/api.interface';
import { Observable, combineLatest } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { ISPFormState } from 'common/dynamic-form/model';
import { DocHelper } from 'utils/dochelper';

import { FieldLabel } from './model';

import { FieldDesc } from '../description/model';
import { FieldSettings } from '../model';

/**
 * Get label from field metadata
 *
 * @param field - field metadata
 * @param doc - doc instance
 */
export function getLabel(field: IField, doc: IFormDocument): string {
  if (field.$noname) {
    return '';
  }

  return DocHelper.getMessage(field.$name, doc);
}

/**
 * Get stream of label from field metadata
 *
 * @param field
 * @param state
 */
export function getLabel$(
  field: IField,
  state: ISPFormState,
): Observable<string> {
  return state.docSubject.pipe(map(doc => getLabel(field, doc)));
}

/**
 * Get label for field component
 *
 * @param field
 * @param doc
 * @param fieldSettings
 */
function getFieldLabel(
  field: IField,
  doc: IFormDocument,
  fieldSettings?: FieldSettings,
): FieldLabel | undefined {
  const labelMsg = getLabel(field, doc);

  if (!labelMsg || fieldSettings?.hideLabel) {
    return;
  }

  const label: FieldLabel = {
    msg: labelMsg,
  };

  if (fieldSettings) {
    label.size = fieldSettings.labelSize;
    label.weight = fieldSettings.labelWeight;
    label.offset = fieldSettings.labelOffset;
  }

  return label;
}

/**
 * Get stream of field label
 *
 * @param field
 * @param state
 * @param fieldSettings$
 * @param desc$
 */
export function getFieldLabel$(
  field: IField,
  state: ISPFormState,
  fieldSettings$: Observable<FieldSettings | undefined>,
  desc$: Observable<FieldDesc | undefined>,
): Observable<FieldLabel | undefined> {
  return combineLatest([state.docSubject, fieldSettings$, desc$]).pipe(
    map(([doc, fieldSettings, desc]) => {
      const label = getFieldLabel(field, doc, fieldSettings);

      if (label && desc?.position === 'label') {
        label.msg += `, ${desc.msg}`;
      }

      if (!label && state.fakeLabels) {
        return { msg: '.', isFake: true };
      }

      return label;
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );
}
